"use client"

import { useEffect, useRef, useState } from "react"
import { MotionDiv } from "@/components/motion"
import Image from "next/image"

const metrics = [
 { value: 25000, suffix: "+", label: "YOUTH IMPACTED", color: "bg-violet" },
 { value: 750, suffix: "+", label: "SCHOOLS & PROGRAMS", color: "bg-violet-dark" },
 { value: 125, suffix: "+", label: "COMMUNITIES REACHED", color: "bg-violet" },
 { value: 1000, suffix: "+", label: "WORKSHOPS DELIVERED", color: "bg-violet" },
]

const partnerLogos = [
 { src: "/images/school-logo-01.png", alt: "School Partner 01" },
 { src: "/images/school-logo-02.png", alt: "School Partner 02" },
 { src: "/images/school-logo-03.png", alt: "School Partner 03" },
 { src: "/images/school-logo-04.png", alt: "School Partner 04" },
 { src: "/images/school-logo-05.png", alt: "School Partner 05" },
 { src: "/images/school-logo-06.png", alt: "School Partner 06" },
 { src: "/images/school-logo-07.png", alt: "School Partner 07" },
 { src: "/images/school-logo-08.png", alt: "School Partner 08" },
 { src: "/images/school-logo-09.png", alt: "School Partner 09" },
 { src: "/images/school-logo-10.png", alt: "School Partner 10" },
 { src: "/images/school-logo-11.png", alt: "School Partner 11" },
 { src: "/images/school-logo-12.png", alt: "School Partner 12" },
 { src: "/images/school-logo-13.png", alt: "School Partner 13" },
 { src: "/images/school-logo-14.png", alt: "School Partner 14" },
 { src: "/images/school-logo-15.png", alt: "School Partner 15" },
 { src: "/images/school-logo-16.png", alt: "School Partner 16" },
 { src: "/images/school-logo-17.png", alt: "School Partner 17" },
  { src: "/images/school-logo-19.png", alt: "School Partner 19" },
 { src: "/images/school-logo-20.png", alt: "School Partner 20" },
 { src: "/images/school-logo-21.png", alt: "School Partner 21" },
 { src: "/images/school-logo-22.png", alt: "School Partner 22" },
 { src: "/images/school-logo-23.png", alt: "School Partner 23" },
 { src: "/images/school-logo-24.png", alt: "School Partner 24" },
 { src: "/images/school-logo-25.png", alt: "School Partner 25" },
]

function AnimatedNumber({ target, suffix }: { target: number; suffix: string }) {
 const [count, setCount] = useState(0)
 const ref = useRef<HTMLSpanElement>(null)
 const hasAnimated = useRef(false)

 useEffect(() => {
  const observer = new IntersectionObserver(
   ([entry]) => {
    if (entry.isIntersecting && !hasAnimated.current) {
     hasAnimated.current = true
     const duration = 2000
     const startTime = performance.now()
     const step = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setCount(Math.floor(target * eased))
      if (progress < 1) requestAnimationFrame(step)
     }
     requestAnimationFrame(step)
    }
   },
   { threshold: 0.5 }
  )
  if (ref.current) observer.observe(ref.current)
  return () => observer.disconnect()
 }, [target])

 return (
  <span ref={ref} className="tabular-nums">
   {count.toLocaleString()}{suffix}
  </span>
 )
}

export default function ImpactMetrics() {
 const [isPaused, setIsPaused] = useState(false)

 return (
  <section className="py-24 overflow-hidden border-t-[3px] border-black bg-white relative">
   {/* Decorative background grid */}
   <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(#f1f5f9 2px, transparent 2px), linear-gradient(90deg, #f1f5f9 2px, transparent 2px)", backgroundSize: "40px 40px" }} />

   <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

    {/* IMPACT SECTION */}
    <div className="text-center mb-16">
     <MotionDiv variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}>
      <h2 className="text-4xl lg:text-5xl text-black font-antonio font-bold tracking-tight uppercase">
       OUR <span className="text-violet">IMPACT</span>
      </h2>
     </MotionDiv>
    </div>

    <MotionDiv className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-24">
     {metrics.map((metric, i) => (
      <div
       key={metric.label}
       className={`relative group flex flex-col items-center justify-center p-8 bg-white border-[3px] border-black shadow-[6px_6px_0px_#000] hover:-translate-y-2 hover:shadow-[10px_10px_0px_#000] transition-all duration-300 transform ${i % 2 === 0 ? 'rotate-1' : '-rotate-1'} hover:rotate-0`}
      >
       {/* Background accent block */}
       <div className={`absolute top-0 left-0 w-full h-2 ${metric.color} border-b-[3px] border-black`} />

       {/* Tape detail */}
       <div className="absolute -top-3 right-4 w-12 h-6 bg-slate-200/80 border border-slate-300 transform rotate-3" />

       <p className="text-5xl lg:text-6xl font-bold text-violet mt-4 mb-2 tracking-tighter" style={{ fontFamily: "var(--font-antonio)" }}>
        <AnimatedNumber target={metric.value} suffix={metric.suffix} />
       </p>
       <p className="text-sm lg:text-base font-bold text-slate-600 tracking-widest uppercase text-center mt-2">
        {metric.label}
       </p>
      </div>
     ))}
    </MotionDiv>

    {/* TRUSTED BY SECTION — carousel */}
    <div className="text-center relative mt-24 pt-2">
     {/* Badge — outside the overflow-hidden box so it isn't cropped */}
     <MotionDiv variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}>
      <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-violet text-white px-6 py-2 uppercase font-antonio tracking-widest text-sm z-30 whitespace-nowrap shadow-[3px_3px_0px_#000]">
       TRUSTED BY SCHOOLS &amp; ORGANIZATIONS
      </div>
     </MotionDiv>

     {/* Logo carousel box */}
     <div className="bg-white border-[3px] border-black shadow-[8px_8px_0px_#000] pt-14 pb-6 px-2 overflow-hidden">
      <div
       className="relative mt-2"
       onMouseEnter={() => setIsPaused(true)}
       onMouseLeave={() => setIsPaused(false)}
      >
       {/* Edge fades */}
       <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent z-10" />
       <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent z-10" />

       {/* Marquee track */}
       <div
        className="flex w-max gap-6 py-2"
        style={{
         animation: "scroll-x 120s linear infinite",
         animationPlayState: isPaused ? "paused" : "running",
        }}
       >
        {[...partnerLogos, ...partnerLogos].map((logo, i) => (
         <div
          key={`${logo.src}-${i}`}
          className="flex items-center justify-center h-24 w-40 shrink-0 transition-all duration-300 hover:scale-110"
         >
          <Image
           src={logo.src}
           alt={logo.alt}
           width={220}
           height={110}
           className="h-20 w-auto object-contain"
           style={{ maxWidth: "180px" }}
          />
         </div>
        ))}
       </div>
      </div>
     </div>
    </div>

   </div>
  </section>
 )
}
